import React, {useCallback, useEffect, useState} from "react";
import {Badge, Icon, useNotifications} from "peggirkit";
import BadgeAction from "peggirkit/dist/components/Badge/BadgeElements/BadgeAction";
import {CheckIcon, DocumentDuplicateIcon} from "@heroicons/react/16/solid";
import {useIntl} from "react-intl";
import {useTooltip} from "@widgets/tooltip/TooltipProvider";
import {notifySuccess} from "@/utils/NotifyUtils";

type Props = {
    code: string,
};

const CodeFormatter = ({code}: Props) => {
    const intl = useIntl();
    const {applyTooltip} = useTooltip();
    const {notify} = useNotifications();
    const [isCopied, setCopied] = useState(false);
    const copyCode = useCallback(() => {
        if (isCopied)
            return;

        navigator
            .clipboard
            .writeText(code)
            .then(() => {
                notifySuccess(intl.formatMessage({id: "copied"}), notify);
                setCopied(true);
            });
    }, [isCopied, code, notify, setCopied, intl,]);

    useEffect(() => {
        if (!isCopied)
            return;

        const t = setTimeout(() => setCopied(false), 3500);
        return () => clearTimeout(t);
    }, [isCopied]);

    return (
        <Badge
            color={"secondary"}
            action={
                <BadgeAction
                    title={intl.formatMessage({id: "copy"})}
                    icon={<Icon icon={isCopied ? CheckIcon : DocumentDuplicateIcon}/>}
                    buttonAction={{
                        as: "button",
                        actionProps: {
                            onClick: copyCode,
                        },
                    }}
                    props={{
                        ...applyTooltip(intl.formatMessage({id: isCopied ? "copied" : "copy"})),
                    }}
                />
            }
        >
            {code}
        </Badge>
    );
};

export default CodeFormatter;